<script setup lang="ts">
import { useElementSize } from '@vueuse/core';

const { t } = useTexts();
const { linkToAppDownload, linkToTelegram } = useLinks();
const { openLogin } = useAuth();

// Detecta bots para melhorar performance em testes
const { isBot } = usePerformanceBotBypass();

const { gamificationActiveAndShow, isRequestPushPermission } =
  storeToRefs(useSmarticoConfigs());

const { requestPushPermission } = useSmarticoInit();

const { showMoneyFormat } = useWallet();
const { parameters } = useParameters();
const { isStandAlone } = useLayout();

const { isGameModalOpen } = storeToRefs(useCasinoStore());
const { isAuthenticated } = storeToRefs(useAuthStore());
const { topbar } = storeToRefs(useLayoutStore());
const { userCanOnlyWithdraw } = useUserLimits();

const { openTopBar, closeTopBar, setTopBarHeight } = useLayoutStore();

const refTopBar = ref<HTMLDivElement | null>(null);

const { height: topBarHeight } = useElementSize(
  refTopBar,
  { height: 0, width: 0 },
  { box: 'border-box' },
);

const expires_days = new Date();

expires_days.setDate(new Date().getDate() + 365);

const isDownloadAppTopBarClosed = useCookie('v2_topbar_download_app_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const isReferralTopBarClosed = useCookie('v2_topbar_referral_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const isTournamentTopBarClosed = useCookie('v2_topbar_tournament_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const isTelegramTopBarClosed = useCookie('v2_topbar_telegram_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const isPushNotifyTopBarClosed = useCookie('v2_topbar_push_notify_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const areAllTopBarClosed = computed<boolean>(() => {
  return (
    isReferralTopBarClosed.value &&
    isDownloadAppTopBarClosed.value &&
    isTournamentTopBarClosed.value &&
    isTelegramTopBarClosed.value &&
    isPushNotifyTopBarClosed.value
  );
});

const referralBonusAmount = computed<number>(() => {
  return parameters.value?.referral_bonus_amount || 0;
});

const formatedBonusAmount = computed(() => {
  return referralBonusAmount.value
    ? showMoneyFormat({ value: referralBonusAmount.value / 100 })
    : '-';
});

const activeReferralBonus = computed<boolean>(() => {
  return !!parameters.value?.referral_bonus_active || false;
});

const bonusType = computed<string>(() => {
  return parameters.value?.referral_bonus_type || 'money_bonus';
});

const formatedBonusType = computed(() => {
  return bonusType.value === 'money_real'
    ? t('account.refer.real').toLocaleUpperCase()
    : t('account.refer.bonus').toLocaleLowerCase();
});

const isDownloadAppActive = computed(() => {
  return (
    !isStandAlone.value &&
    !!linkToAppDownload.value &&
    !isDownloadAppTopBarClosed.value
  );
});

const isReferralBonusActive = computed(() => {
  if (isBetBr() && !isAuthenticated.value) {
    return false;
  }

  return (
    referralBonusAmount.value &&
    activeReferralBonus.value &&
    !isReferralTopBarClosed.value
  );
});

const isTournamentActive = computed(() => {
  return !!gamificationActiveAndShow.value && !isTournamentTopBarClosed.value;
});

const isTelegramActive = computed(() => {
  return !!linkToTelegram.value && !isTelegramTopBarClosed.value;
});

const isPushNotifyActive = computed(() => {
  return (
    !!isRequestPushPermission.value &&
    !isPushNotifyTopBarClosed.value &&
    !isNotificationGrantedOrDenied.value
  );
});

const hideTopBar = computed(() => {
  return (
    !topbar.value.isVisible ||
    isGameModalOpen.value ||
    userCanOnlyWithdraw.value
  );
});

const isThereAnyTopBarActive = computed(() => {
  // Oculta TopBar para bots (melhora performance em testes)
  if (isBot.value) {
    return false;
  }

  return (
    isDownloadAppActive.value ||
    isReferralBonusActive.value ||
    isTournamentActive.value ||
    isTelegramActive.value ||
    isPushNotifyActive.value
  );
});

const referalBonusText = computed(() => {
  if (parameters.value?.active_message_bonus_indication) {
    return parameters.value?.message_bonus_indication;
  }

  return t('components.top_bar.refer_friend', {
    amount: formatedBonusAmount.value,
    type: formatedBonusType.value,
  });
});

const referalButtonText = computed(() => {
  if (parameters.value?.active_text_button_indication) {
    return parameters.value?.text_button_indication;
  }

  return t('components.top_bar.refer_action');
});

const currentContent = computed(() => {
  if (isReferralBonusActive.value) {
    return {
      emoji: '💥',
      msg: referalBonusText.value,
      btnText: referalButtonText.value,
      onClose: () => {
        isReferralTopBarClosed.value = true;
      },
    };
  } else if (isDownloadAppActive.value) {
    return {
      emoji: '📲',
      msg: t('components.top_bar.app_download_msg'),
      btnText: t('components.top_bar.download_action'),
      onClose: () => {
        isDownloadAppTopBarClosed.value = true;
      },
    };
  } else if (isTournamentActive.value) {
    return {
      emoji: '🏆',
      msg: t('components.top_bar.tournament_msg'),
      btnText: t('components.top_bar.tournament_action'),
      onClose: () => {
        isTournamentTopBarClosed.value = true;
      },
    };
  } else if (isTelegramActive.value) {
    return {
      emoji: '💬',
      msg: t('components.top_bar.telegram_msg'),
      btnText: t('components.top_bar.telegram_action'),
      onClose: () => {
        isTelegramTopBarClosed.value = true;
      },
    };
  } else if (isPushNotifyActive.value) {
    return {
      emoji: '🔔',
      msg: t('components.top_bar.push_msg'),
      btnText: t('components.top_bar.push_action'),
      onClose: () => {
        isPushNotifyTopBarClosed.value = true;
      },
    };
  }
});

const isNotificationGrantedOrDenied = computed(() => {
  const isSupported = () =>
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  if (!isSupported()) {
    return true;
  }

  return (
    Notification?.permission === 'granted' ||
    Notification?.permission === 'denied'
  );
});

function onTopBarClick() {
  if (isReferralBonusActive.value) {
    if (!isAuthenticated.value) {
      openLogin();

      return;
    }

    navigateTo(localeLink('/user/refers'));

    return;
  }

  if (isDownloadAppActive.value) {
    window.open(linkToAppDownload.value, '_blank');

    return;
  }

  if (isTournamentActive.value) {
    if (!isAuthenticated.value) {
      openLogin();

      return;
    }

    navigateTo(localeLink('/vip/tournaments'));

    return;
  }

  if (isTelegramActive.value) {
    window.open(linkToTelegram.value, '_blank');

    return;
  }

  if (isPushNotifyActive.value) {
    if (!isAuthenticated.value) {
      openLogin();

      return;
    }

    requestPushPermission();
  }
}

function handleCloseTopBar() {
  currentContent.value?.onClose();

  if (!isThereAnyTopBarActive.value) {
    closeTopBar();
  }
}

watch(
  areAllTopBarClosed,
  (areClosed: boolean) => {
    if (areClosed) {
      handleCloseTopBar();
    } else {
      openTopBar();
    }
  },
  { immediate: true },
);

watch(
  referralBonusAmount,
  (newAmountValue) => {
    if (!newAmountValue) {
      return setTopBarHeight(0);
    }

    if (!areAllTopBarClosed.value) {
      return openTopBar();
    }
  },
  {
    immediate: true,
  },
);

watch(topBarHeight, (newHeight) => {
  if (isThereAnyTopBarActive.value) {
    setTopBarHeight(newHeight);
  } else {
    setTopBarHeight(0);
  }
});

onMounted(() => {
  if (isThereAnyTopBarActive.value) {
    setTopBarHeight(topBarHeight.value);
  } else {
    setTopBarHeight(0);
  }
});
</script>

<template>
  <div
    v-if="isThereAnyTopBarActive && !hideTopBar"
    id="divTopBar"
    ref="refTopBar"
    :class="$style.topbar"
  >
    <div :class="$style.emoji">
      {{ currentContent?.emoji }}
    </div>
    <span @click="onTopBarClick()">
      {{ currentContent?.msg }}
    </span>
    <button @click="onTopBarClick()">
      {{ currentContent?.btnText }}
    </button>
    <div
      :class="$style.closeButton"
      @click.prevent="handleCloseTopBar()"
    >
      <NuxtIcon name="close" />
    </div>
  </div>
</template>

<style module lang="postcss">
.topbar {
  @apply w-full bg-sidebar-cta-bg text-sidebar-cta-text flex items-center justify-center gap-3 md:gap-2 py-1 md:py-1.5 pl-2 pr-8 md:px-2 leading-3;

  @media print {
    & {
      display: none;
    }
  }

  .emoji {
    @apply text-xl -mr-0.5;
  }

  span {
    @apply flex items-center min-h-7 cursor-pointer font-medium text-2xs md:text-sm line-clamp-2;

    @media (max-width: 395px) {
      @apply text-[.68rem] leading-3;
    }
    @media (max-width: 380px) {
      @apply text-[.62rem];
    }
    @media (max-width: 360px) {
      @apply text-[.58rem];
    }
    @media (max-width: 330px) {
      @apply text-[.52rem];
    }
    /* @apply max-w-[80%] overflow-hidden whitespace-nowrap; */
  }

  button {
    @apply bg-sidebar-cta-text text-sidebar-cta-bg px-2 py-1 md:py-0.5 rounded-full text-2xs md:text-xs font-semibold;
  }

  .closeButton {
    @apply absolute right-0 md:right-2 p-2 opacity-60 hover:opacity-100 cursor-pointer;

    svg {
      @apply fill-texts text-sm md:text-lg;
    }
  }
}
</style>
